import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getConfig, type RuntimeLocale } from "../config";
import { he, messageForFrom } from "./he";
import { en } from "./en";

export type SupportedLocale = "he" | "en";
export type TextDirection = "rtl" | "ltr";

const dictionaries = { he, en } as const;
const STORAGE_KEY = "vc.locale";

type LocaleStrings = typeof dictionaries[SupportedLocale];

interface I18nContextValue {
  locale: SupportedLocale;
  dir: TextDirection;
  t: LocaleStrings;
  setLocale: (locale: SupportedLocale) => void;
  toggleLocale: () => void;
  messageFor: (code: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function isSupportedLocale(value: unknown): value is SupportedLocale {
  return value === "he" || value === "en";
}

function readStoredLocale(): SupportedLocale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isSupportedLocale(value) ? value : null;
  } catch {
    return null;
  }
}

function writeStoredLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Embedded/hardened browsers may block storage. The in-memory choice still works.
  }
}

function browserLocale(): SupportedLocale {
  const langs = (typeof navigator !== "undefined" && navigator.languages?.length)
    ? navigator.languages
    : [typeof navigator !== "undefined" ? navigator.language : ""];
  const normalized = langs.map((l) => l.toLowerCase());
  return normalized.some((l) => l.startsWith("he")) ? "he" : "en";
}

export function resolveInitialLocale(configLocale: RuntimeLocale = "auto"): SupportedLocale {
  const stored = typeof window !== "undefined" ? readStoredLocale() : null;
  if (stored) return stored;
  if (isSupportedLocale(configLocale)) return configLocale;
  return browserLocale();
}

export function directionFor(locale: SupportedLocale): TextDirection {
  return locale === "he" ? "rtl" : "ltr";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => resolveInitialLocale(getConfig().locale));

  const setLocale = (next: SupportedLocale) => {
    setLocaleState(next);
    writeStoredLocale(next);
  };

  const dir = directionFor(locale);
  const t = dictionaries[locale];

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    document.body.dir = dir;
    document.title = t.app.name;
  }, [dir, locale, t.app.name]);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    dir,
    t,
    setLocale,
    toggleLocale: () => setLocale(locale === "he" ? "en" : "he"),
    messageFor: (code: string) => messageForFrom(t, code)
  }), [dir, locale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
